import { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import _ from 'lodash';

import { Copay } from '@/modules/ExpressSetup/ExpressSetup.types.ts';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { usePlansStore } from '@/modules/Renewals/RenewalSetup/stores';

import { HealthPlanRenewal, Renewal, RenewalPlanDto } from '../../renewals.types';
import { toRawConfig } from '../PlanYearPlansStep/PayrollCalendarSection/utils/renewalPayrollCalendarAdapter';
import { useUpdateRenewalSetupByIdQuery } from '../queries';
import { CopaysOptionValues, RenewalPlanOptionValues } from '../RenewalSetup.types';
import { useRenewalSetupStore } from '../stores';
import { useCopayStore } from '../stores/useCopay.store';
import { useHealthPlansStore } from '../stores/useHealthPlans.store';
import usePayrollCalendarsStore from '../stores/usePayrollCalendars.store';
import { EditablePlan } from '../stores/usePlans.store';

const getNumberWithZero = (value?: string | number) =>
  value === '' || value === undefined || value === null ? undefined : _.toNumber(value);

export const useUpdateRenewalSetup = ({
  renewalSetupData,
  handleSetPromptDisabled,
  onSuccess,
  onError,
}: {
  renewalSetupData?: Renewal;
  handleSetPromptDisabled?: (value: boolean) => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
} = {}) => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const isHealthPlanRenewalEnabled = useFeatureFlag(FeatureFlags.HEALTH_PLAN_RENEWAL);
  const isRenewalCalendarEnabled = useFeatureFlag(FeatureFlags.RENEWAL_CALENDAR);
  const mutation = useUpdateRenewalSetupByIdQuery();
  const renewalSelectedOption = useRenewalSetupStore((state) => state.plansPlanYear.selectedOption);
  const copaysSelectedOption = useRenewalSetupStore((state) => state.copays.selectedOption);
  const planDetailsNote = useRenewalSetupStore((state) => state.planDetailsNote);
  const healthPlanDetailsNote = useRenewalSetupStore((state) => state.healthPlanDetailsNote);
  const healthPlansSelectedOption = useRenewalSetupStore((state) => state.healthPlans.selectedOption);

  const copays = useCopayStore((state) => state.state.copays);
  const plans = usePlansStore((state) => state.state.plans);
  const healthPlans = useHealthPlansStore((state) => state.state.healthPlans);
  const payrollCalendars = usePayrollCalendarsStore((state) => state.state.payrollCalendars);
  const formattedPlans = useMemo(() => {
    return renewalSetupData?.plansDto?.map((planDto: RenewalPlanDto) => {
      const matchedPlan = plans?.find((plan: EditablePlan) => plan.id === _.toString(planDto.prior_plan_id));

      return matchedPlan
        ? {
            ...planDto,
            plan_code: matchedPlan.planCode.trim(),
            name: matchedPlan.planName,
          }
        : planDto;
    });
  }, [renewalSetupData?.plansDto, plans]);

  const formattedCopays = useMemo(() => {
    if (copaysSelectedOption === CopaysOptionValues.ACTIVE) {
      return renewalSetupData?.copays?.map((copay: Copay) => {
        return {
          amount: getNumberWithZero(copay.amount),
          category_type: copay?.categoryType,
        };
      });
    }
    if (copaysSelectedOption === CopaysOptionValues.REQUESTED) {
      return copays?.map((copay: Copay) => {
        return {
          amount: getNumberWithZero(copay.amount),
          category_type: copay?.categoryType,
        };
      });
    }
    return [];
  }, [copays, copaysSelectedOption, renewalSetupData?.copays]);

  const formattedNote = useMemo(() => {
    if (
      renewalSelectedOption === RenewalPlanOptionValues.REQUESTED ||
      renewalSelectedOption === RenewalPlanOptionValues.CANCELED
    ) {
      return planDetailsNote;
    }
    return '';
  }, [renewalSelectedOption, planDetailsNote]);

  const formattedHealthPlanNote = useMemo(() => {
    if (
      healthPlansSelectedOption === RenewalPlanOptionValues.REQUESTED ||
      healthPlansSelectedOption === RenewalPlanOptionValues.CANCELED
    ) {
      return healthPlanDetailsNote;
    }
    return '';
  }, [healthPlansSelectedOption, healthPlanDetailsNote]);
  const isHealthPlansStepActive = isHealthPlanRenewalEnabled && (healthPlans?.length || 0) > 0;

  const formattedStatus = useMemo(() => {
    // Plans CANCELED → skip all, send CANCELED
    if (renewalSelectedOption === RenewalPlanOptionValues.CANCELED) {
      return RenewalPlanOptionValues.CANCELED;
    }
    if (renewalSelectedOption === RenewalPlanOptionValues.ACTIVE) {
      if (!isHealthPlansStepActive || healthPlansSelectedOption === RenewalPlanOptionValues.ACTIVE) {
        return RenewalPlanOptionValues.ACTIVE;
      }
    }
    // All other combinations → send REQUESTED
    return RenewalPlanOptionValues.REQUESTED;
  }, [renewalSelectedOption, healthPlansSelectedOption, isHealthPlansStepActive]);

  const formattedHealthPlans = useMemo(() => {
    if (!healthPlans?.length) return undefined;

    return healthPlans.flatMap((hp: HealthPlanRenewal) => {
      const raw = hp.rawDto;

      if (!raw) return [];

      const editedTiers = hp.processingTiers || [];
      const editedConfigs = hp.deductibleConfigs || [];

      return {
        ...raw,
        name: hp.name ?? raw.name,
        plan_code: hp.planCode ?? raw.plan_code,
        processing_tiers: (raw.processing_tiers || []).map((rawTier, tierIdx) => {
          const editedTier = editedTiers[tierIdx];
          const rawServiceConfigs = rawTier.service_configs || [];
          return {
            ...rawTier,
            // card_purse_lock_plan_names is editable in the UI.
            card_purse_lock_plan_names:
              editedTier?.cardPurseLockPlanNames ?? rawTier.card_purse_lock_plan_names,
            service_configs: rawServiceConfigs.map((rawSvc, svcIdx) => {
              const editedSvc = editedTier?.serviceConfigs?.[svcIdx];
              const rawRules = rawSvc.payment_rules || [];
              return {
                ...rawSvc,
                payment_rules: rawRules.map((rawRule, ruleIdx) => {
                  const editedRule = editedSvc?.paymentRules?.[ruleIdx];
                  return {
                    ...rawRule,
                    percentage: editedRule?.percentage ?? rawRule.percentage,
                    max_amount: editedRule?.maxAmount ?? rawRule.max_amount,
                  };
                }),
              };
            }),
          };
        }),
        deductible_configs: (raw.deductible_configs || []).map((rawConfig, configIdx) => {
          const editedConfig = editedConfigs[configIdx];
          return {
            ...rawConfig,
            trigger_configs: (rawConfig.trigger_configs || []).map((rawTrigger, triggerIdx) => {
              const editedTrigger = editedConfig?.triggerConfigs?.[triggerIdx];
              return {
                ...rawTrigger,
                // threshold_amount is editable in the UI.
                threshold_amount: editedTrigger?.thresholdAmount ?? rawTrigger.threshold_amount,
              };
            }),
            auto_funding_configs: (rawConfig.auto_funding_configs || []).map((rawFunding, fundingIdx) => {
              const editedFunding = editedConfig?.autoFundingConfigs?.[fundingIdx];
              return {
                ...rawFunding,
                // auto_enrollment_amount and per_person_spend_limit_amount are editable in the UI.
                auto_enrollment_amount:
                  editedFunding?.autoEnrollmentAmount ?? rawFunding.auto_enrollment_amount,
                per_person_spend_limit_amount:
                  editedFunding?.perPersonSpendLimitAmount ?? rawFunding.per_person_spend_limit_amount,
              };
            }),
            category_spend_limits: (rawConfig.category_spend_limits || []).map((rawCsl, cslIdx) => {
              const editedCsl = editedConfig?.categorySpendLimits?.[cslIdx];
              return {
                ...rawCsl,
                // category_spend_limit is editable in the UI.
                category_spend_limit: editedCsl?.categorySpendLimit ?? rawCsl.category_spend_limit,
              };
            }),
          };
        }),
      };
    });
  }, [healthPlans]);

  const formattedPayrollCalendars = useMemo(() => {
    if (!payrollCalendars?.length) return undefined;

    return payrollCalendars.map((calendar) => {
      const raw = calendar.rawDto;
      const effectiveConfig = calendar.config ? toRawConfig(calendar.config) : raw.config;

      return {
        ...raw,
        // Override only the four user-editable fields
        name: calendar.name ?? raw.name,
        frequency_type: calendar.frequencyType ?? raw.frequency_type,
        config: effectiveConfig,
        schedule: calendar.schedule ?? (raw.schedule ? raw.schedule.map(String) : []),
      };
    });
  }, [payrollCalendars]);

  const handleSave = useCallback(async () => {
    const payload = {
      id,
      status: formattedStatus,
      plan_renewal_note: formattedNote,
      ...(isHealthPlanRenewalEnabled && { health_plan_renewal_note: formattedHealthPlanNote }),
      renewal_json: {
        copays: formattedCopays,
        new_plan_year: renewalSetupData?.newPlanYearDto,
        plans: formattedPlans,
        ...(isHealthPlanRenewalEnabled && { health_plans: formattedHealthPlans }),
        ...(isRenewalCalendarEnabled &&
          formattedPayrollCalendars && { payroll_calendars: formattedPayrollCalendars }),
      },
    };
    try {
      await mutation.mutateAsync(payload);
      handleSetPromptDisabled?.(true);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  }, [
    id,
    isHealthPlanRenewalEnabled,
    isRenewalCalendarEnabled,
    formattedStatus,
    formattedNote,
    formattedHealthPlanNote,
    formattedCopays,
    renewalSetupData?.newPlanYearDto,
    formattedPlans,
    formattedHealthPlans,
    formattedPayrollCalendars,
    mutation,
    handleSetPromptDisabled,
    onSuccess,
    onError,
  ]);

  return {
    handleSave,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
